import type {
  MeResponse,
  CurrentUser,
  OnboardRequest,
  OnboardResponse,
  ListMembersResponse,
  AccountMember,
  AccountSummary,
  InviteMemberRequest,
  InviteMemberResponse,
  CreateAccountRequest,
  CreateAccountResponse,
  SwitchAccountPlanRequest,
  SwitchAccountPlanResponse,
  AccountPlanConflictBody,
} from '../../../../shared/types/accounts';
import type { PlanViolation } from '../../../../shared/types/projects';
import { authFetch } from './authFetch';
import { API_BASE } from './version';

async function readErr(r: Response, fallback: string): Promise<string> {
  try {
    const j = (await r.json()) as { error?: { message?: string } };
    if (j?.error?.message) return j.error.message;
  } catch { /* */ }
  return fallback;
}

/** GET /api/v1/auth/me — identity + every account the user belongs to. */
export async function fetchMe(): Promise<CurrentUser> {
  const r = await authFetch(`${API_BASE}/auth/me`);
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load identity'));
  return ((await r.json()) as MeResponse).data;
}

/**
 * PUT /api/v1/accounts/active — persist the caller's active workspace so the
 * backend scopes to it even when a request omits `?account=`. Fire-and-forget
 * from the switcher; failures are non-fatal (the `?account=` param still
 * narrows this session).
 */
export async function setActiveWorkspace(accountId: string): Promise<void> {
  const r = await authFetch(`${API_BASE}/accounts/active`, {
    method: 'PUT',
    body: JSON.stringify({ accountId }),
  });
  if (!r.ok) throw new Error(await readErr(r, 'Failed to set active workspace'));
}

/** POST /api/v1/auth/onboard — claim or join an account. Idempotent. */
export async function onboardAccount(req: OnboardRequest): Promise<OnboardResponse['data']> {
  const r = await authFetch(`${API_BASE}/auth/onboard`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!r.ok) throw new Error(await readErr(r, 'Onboarding failed'));
  return ((await r.json()) as OnboardResponse).data;
}

/**
 * POST /api/v1/accounts — create an additional workspace owned by the
 * authenticated user. Distinct from /auth/onboard (first-time bootstrap).
 */
export async function createAccount(req: CreateAccountRequest): Promise<AccountSummary> {
  const r = await authFetch(`${API_BASE}/accounts`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!r.ok) throw new Error(await readErr(r, 'Could not create workspace'));
  return ((await r.json()) as CreateAccountResponse).data;
}

export async function fetchAccountMembers(accountId: string): Promise<AccountMember[]> {
  const r = await authFetch(`${API_BASE}/accounts/${encodeURIComponent(accountId)}/members`);
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load members'));
  return ((await r.json()) as ListMembersResponse).data.members;
}

export async function inviteAccountMember(
  accountId: string,
  req: InviteMemberRequest,
): Promise<AccountMember> {
  const r = await authFetch(`${API_BASE}/accounts/${encodeURIComponent(accountId)}/members`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!r.ok) throw new Error(await readErr(r, 'Invite failed'));
  return ((await r.json()) as InviteMemberResponse).data;
}

export async function removeAccountMember(accountId: string, email: string): Promise<void> {
  const r = await authFetch(
    `${API_BASE}/accounts/${encodeURIComponent(accountId)}/members/${encodeURIComponent(email)}`,
    { method: 'DELETE' },
  );
  if (!r.ok) throw new Error(await readErr(r, 'Remove failed'));
}

// ---------------------------------------------------------------------------
// Plan switching
// ---------------------------------------------------------------------------

/**
 * Thrown when the server returns 422 PLAN_VIOLATION for an account plan switch.
 * Carries per-project violations so the UI can tell the user exactly which
 * projects need cleanup before the downgrade can proceed.
 */
export class AccountPlanViolationError extends Error {
  public readonly violations: PlanViolation[];
  constructor(message: string, violations: PlanViolation[]) {
    super(message);
    this.name = 'AccountPlanViolationError';
    this.violations = violations;
  }
}

/** PATCH /api/v1/accounts/:id/onboarding — update wizard step */
export async function patchOnboarding(
  accountId: string,
  req: { step: string; completedAt?: string | null },
): Promise<void> {
  const r = await authFetch(
    `${API_BASE}/accounts/${encodeURIComponent(accountId)}/onboarding`,
    { method: 'PATCH', body: JSON.stringify(req) },
  );
  if (!r.ok) throw new Error(await readErr(r, 'Failed to update onboarding step'));
}

/** POST /api/v1/projects — create the first project during onboarding */
export async function createFirstProject(
  accountId: string,
  name: string,
): Promise<{ id: string; name: string }> {
  // Derive a project id from the account id
  const id = accountId.slice(0, 32).replace(/[^a-z0-9-]/g, '-').replace(/^-+|-+$/g, '');
  const r = await authFetch(`${API_BASE}/projects`, {
    method: 'POST',
    body: JSON.stringify({ id: id || 'default-project', name, accountId }),
  });
  if (!r.ok) throw new Error(await readErr(r, 'Could not create project'));
  const data = (await r.json()) as { data: { id: string; name: string } };
  return data.data;
}

/** PUT /api/v1/accounts/:id/plan */
export async function switchAccountPlan(
  accountId: string,
  req: SwitchAccountPlanRequest,
): Promise<AccountSummary> {
  const r = await authFetch(
    `${API_BASE}/accounts/${encodeURIComponent(accountId)}/plan`,
    { method: 'PUT', body: JSON.stringify(req) },
  );

  if (r.status === 422) {
    let body: AccountPlanConflictBody | null = null;
    try { body = (await r.json()) as AccountPlanConflictBody; } catch { /* */ }
    throw new AccountPlanViolationError(
      body?.error.message ?? 'Plan downgrade would exceed current limits.',
      body?.error.violations ?? [],
    );
  }

  if (!r.ok) throw new Error(await readErr(r, `Failed to switch plan: ${r.statusText}`));
  return ((await r.json()) as SwitchAccountPlanResponse).data;
}
